import React, { useEffect, useState } from "react";
import InfoTooltip from "../poster/InfoTooltip";
import LayoutDiagramModal from "../poster/LayoutDiagramModal";

const BAR_LOGO_TYPES = ["white_logo", "dark_logo", "no_logo", "transparent"];
const ORIENTATIONS = ["landscape", "portrait", "instagram"];

const ORIENTATION_DEFAULTS = {
  landscape: {
    page_width: 3008, page_height: 1700,
    font_size_primary: 30, font_size_secondary: 17, font_size_matchday: 42,
    font_size_date_day: 112, font_size_date_month: 34, font_size_title: 72,
    page_margin_side: 280, page_margin_side_extra: 50,
    page_margin_top: 50, page_margin_bottom: 80,
    logo_position_y: 1555, logo_margin_top: 150,
    title_margin_top: 10, title_margin_bottom: 160,
    portion_width: 1000, portion_width_single_column: 1400,
    portion_line_height: 15, time_margin_right: 20,
    portion_vertical_spacing: 20,
    entry_margin_bottom: 10, matchday_margin_bottom: 3,
    single_column_extra_margin: 50,
  },
  portrait: {
    page_width: 1700, page_height: 2404,
    font_size_primary: 30, font_size_secondary: 17, font_size_matchday: 42,
    font_size_date_day: 112, font_size_date_month: 34, font_size_title: 72,
    page_margin_side: 160, page_margin_side_extra: 50,
    page_margin_top: 50, page_margin_bottom: 80,
    logo_position_y: 2150, logo_margin_top: 10,
    title_margin_top: 100, title_margin_bottom: 30,
    portion_width: 605, portion_width_single_column: 1105,
    portion_line_height: 15, time_margin_right: 20,
    portion_vertical_spacing: 20,
    entry_margin_bottom: 10, matchday_margin_bottom: 3,
    single_column_extra_margin: 50,
  },
  instagram: {
    page_width: 1700, page_height: 2404,
    font_size_primary: 30, font_size_secondary: 17, font_size_matchday: 42,
    font_size_date_day: 112, font_size_date_month: 34, font_size_title: 72,
    page_margin_side: 160, page_margin_side_extra: 50,
    page_margin_top: 50, page_margin_bottom: 80,
    logo_position_y: 2150, logo_margin_top: 10,
    title_margin_top: 100, title_margin_bottom: 30,
    portion_width: 605, portion_width_single_column: 1105,
    portion_line_height: 15, time_margin_right: 20,
    portion_vertical_spacing: 20,
    entry_margin_bottom: 10, matchday_margin_bottom: 3,
    single_column_extra_margin: 50,
  },
};

function resolvePresetDimensions(rawW, rawH, orient) {
  if (orient === "landscape") {
    return rawW >= rawH ? [rawW, rawH] : [rawH, rawW];
  }
  return rawW <= rawH ? [rawW, rawH] : [rawH, rawW];
}

function calculateProportionalValues(w, h, orient) {
  const defaults = ORIENTATION_DEFAULTS[orient] || ORIENTATION_DEFAULTS.landscape;
  const scaleW = w / defaults.page_width;
  const scaleH = h / defaults.page_height;
  const scale = Math.min(scaleW, scaleH);

  const scalePx = (v) => Math.round(v * scale);

  return {
    font_size_primary: scalePx(defaults.font_size_primary),
    font_size_secondary: scalePx(defaults.font_size_secondary),
    font_size_matchday: scalePx(defaults.font_size_matchday),
    font_size_date_day: scalePx(defaults.font_size_date_day),
    font_size_date_month: scalePx(defaults.font_size_date_month),
    font_size_title: scalePx(defaults.font_size_title),
    page_margin_side: Math.round(defaults.page_margin_side * scaleW),
    page_margin_side_extra: Math.round(defaults.page_margin_side_extra * scaleW),
    page_margin_top: Math.round(defaults.page_margin_top * scaleH),
    page_margin_bottom: Math.round(defaults.page_margin_bottom * scaleH),
    logo_position_y: Math.round(defaults.logo_position_y * scaleH),
    logo_margin_top: Math.round(defaults.logo_margin_top * scaleH),
    title_margin_top: Math.round(defaults.title_margin_top * scaleH),
    title_margin_bottom: Math.round(defaults.title_margin_bottom * scaleH),
    portion_width: Math.round(defaults.portion_width * scaleW),
    portion_width_single_column: Math.round(defaults.portion_width_single_column * scaleW),
    portion_line_height: Math.round(defaults.portion_line_height * scale),
    time_margin_right: Math.round(defaults.time_margin_right * scaleW),
    portion_vertical_spacing: Math.round(defaults.portion_vertical_spacing * scale),
    entry_margin_bottom: Math.round(defaults.entry_margin_bottom * scale),
    matchday_margin_bottom: Math.round(defaults.matchday_margin_bottom * scale),
    single_column_extra_margin: Math.round(defaults.single_column_extra_margin * scaleW),
  };
}

export default function PosterModal({
  mode = "create",
  poster = null,
  systemFonts = [],
  posterPresets = [],
  onSave,
  onClose,
}) {
  const isEdit = mode === "edit";

  const [name, setName] = useState("");
  const [barLogoType, setBarLogoType] = useState("");
  const [orientation, setOrientation] = useState("");
  const [color1, setColor1] = useState("#3b82f6");
  const [color2, setColor2] = useState("#3b82f6");
  const [color3, setColor3] = useState("#3b82f6");
  const [backgroundColor, setBackgroundColor] = useState("#ffffff");
  // TODO: uncomment when Favorite reaches production (currently on staging in testing phase)
  // const [favoriteStarFill, setFavoriteStarFill] = useState("#d4af37");
  // const [favoriteStarStroke, setFavoriteStarStroke] = useState("#b7901e");
  // const [favoriteStarStrokeWidth, setFavoriteStarStrokeWidth] = useState(1);
  // const [favoriteStarSize, setFavoriteStarSize] = useState(28);
  // const [favoriteStarGap, setFavoriteStarGap] = useState(10);
  const [pps, setPps] = useState(false);
  const [video, setVideo] = useState(false);
  const [duration, setDuration] = useState(15);

  const [showAdvanced, setShowAdvanced] = useState(false);
  const [selectedPresetId, setSelectedPresetId] = useState("");
  const [systemFontId, setSystemFontId] = useState("");
  const [nrOfColumns, setNrOfColumns] = useState(2);

  const [fontSizePrimary, setFontSizePrimary] = useState("");
  const [fontSizeSecondary, setFontSizeSecondary] = useState("");
  const [fontSizeMatchday, setFontSizeMatchday] = useState("");
  const [fontSizeDateDay, setFontSizeDateDay] = useState("");
  const [fontSizeDateMonth, setFontSizeDateMonth] = useState("");
  const [fontSizeTitle, setFontSizeTitle] = useState("");

  const [pageWidth, setPageWidth] = useState("");
  const [pageHeight, setPageHeight] = useState("");
  const [pageMarginSide, setPageMarginSide] = useState("");
  const [pageMarginSideExtra, setPageMarginSideExtra] = useState("");
  const [pageMarginTop, setPageMarginTop] = useState("");
  const [pageMarginBottom, setPageMarginBottom] = useState("");

  const [logoPositionY, setLogoPositionY] = useState("");
  const [logoMarginTop, setLogoMarginTop] = useState("");

  const [titleMarginTop, setTitleMarginTop] = useState("");
  const [titleMarginBottom, setTitleMarginBottom] = useState("");

  const [portionWidth, setPortionWidth] = useState("");
  const [portionWidthSingleColumn, setPortionWidthSingleColumn] = useState("");
  const [portionLineHeight, setPortionLineHeight] = useState("");
  const [timeMarginRight, setTimeMarginRight] = useState("");
  const [portionVerticalSpacing, setPortionVerticalSpacing] = useState("");

  const [entryMarginBottom, setEntryMarginBottom] = useState("");
  const [matchdayMarginBottom, setMatchdayMarginBottom] = useState("");
  const [entryMaxLines, setEntryMaxLines] = useState("");
  const [minEntriesForSplit, setMinEntriesForSplit] = useState("");
  const [minEntriesPerColumnSplit, setMinEntriesPerColumnSplit] = useState("");

  const [singleColumnExtraMargin, setSingleColumnExtraMargin] = useState("");
  const [maxZoomMultiplier, setMaxZoomMultiplier] = useState("");

  const [fileBkg, setFileBkg] = useState(null);
  const [fileExample, setFileExample] = useState(null);
  const [fileLogo, setFileLogo] = useState(null);
  const [fileCustomFont, setFileCustomFont] = useState(null);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [recalcMessage, setRecalcMessage] = useState("");
  const [showDiagram, setShowDiagram] = useState(false);

  useEffect(() => {
    setName(poster?.name || "");
    setBarLogoType(poster?.bar_logo_type || "");
    setOrientation(poster?.orientation || "");
    setColor1(poster?.color1 || "#3b82f6");
    setColor2(poster?.color2 || "#3b82f6");
    setColor3(poster?.color3 || "#3b82f6");
    setBackgroundColor(poster?.color_bkg || "#ffffff");
    // TODO: uncomment when Favorite reaches production (currently on staging in testing phase)
    // setFavoriteStarFill(poster?.favorite_star_fill || "#d4af37");
    // setFavoriteStarStroke(poster?.favorite_star_stroke || "#b7901e");
    // setFavoriteStarStrokeWidth(poster?.favorite_star_stroke_width ?? 1);
    // setFavoriteStarSize(poster?.favorite_star_size ?? 28);
    // setFavoriteStarGap(poster?.favorite_star_gap ?? 10);
    setPps(!!poster?.pps_slideshow);
    setVideo(!!poster?.video_slideshow);
    setDuration(poster?.slide_duration ?? 15);
    setSystemFontId(poster?.system_font_id ?? "");
    setNrOfColumns(poster?.nr_of_columns ?? 2);
    setFontSizePrimary(poster?.font_size_primary ?? "");
    setFontSizeSecondary(poster?.font_size_secondary ?? "");
    setFontSizeMatchday(poster?.font_size_matchday ?? "");
    setFontSizeDateDay(poster?.font_size_date_day ?? "");
    setFontSizeDateMonth(poster?.font_size_date_month ?? "");
    setFontSizeTitle(poster?.font_size_title ?? "");
    setPageWidth(poster?.page_width ?? "");
    setPageHeight(poster?.page_height ?? "");
    setPageMarginSide(poster?.page_margin_side ?? "");
    setPageMarginSideExtra(poster?.page_margin_side_extra ?? "");
    setPageMarginTop(poster?.page_margin_top ?? "");
    setPageMarginBottom(poster?.page_margin_bottom ?? "");
    setLogoPositionY(poster?.logo_position_y ?? "");
    setLogoMarginTop(poster?.logo_margin_top ?? "");
    setTitleMarginTop(poster?.title_margin_top ?? "");
    setTitleMarginBottom(poster?.title_margin_bottom ?? "");
    setPortionWidth(poster?.portion_width ?? "");
    setPortionWidthSingleColumn(poster?.portion_width_single_column ?? "");
    setPortionLineHeight(poster?.portion_line_height ?? "");
    setTimeMarginRight(poster?.time_margin_right ?? "");
    setPortionVerticalSpacing(poster?.portion_vertical_spacing ?? "");
    setEntryMarginBottom(poster?.entry_margin_bottom ?? "");
    setMatchdayMarginBottom(poster?.matchday_margin_bottom ?? "");
    setEntryMaxLines(poster?.entry_max_lines ?? "");
    setMinEntriesForSplit(poster?.min_entries_for_split ?? "");
    setMinEntriesPerColumnSplit(poster?.min_entries_per_column_split ?? "");
    setSingleColumnExtraMargin(poster?.single_column_extra_margin ?? "");
    setMaxZoomMultiplier(poster?.max_zoom_multiplier ?? "");
    setSelectedPresetId("");
    setFileBkg(null);
    setFileExample(null);
    setFileLogo(null);
    setFileCustomFont(null);
    setError("");
    setRecalcMessage("");
  }, [poster]);

  function applyProportionalValues(scaled, { includeFonts = true } = {}) {
    if (includeFonts) {
      setFontSizePrimary(scaled.font_size_primary);
      setFontSizeSecondary(scaled.font_size_secondary);
      setFontSizeMatchday(scaled.font_size_matchday);
      setFontSizeDateDay(scaled.font_size_date_day);
      setFontSizeDateMonth(scaled.font_size_date_month);
      setFontSizeTitle(scaled.font_size_title);
    }
    setPageMarginSide(scaled.page_margin_side);
    setPageMarginSideExtra(scaled.page_margin_side_extra);
    setPageMarginTop(scaled.page_margin_top);
    setPageMarginBottom(scaled.page_margin_bottom);
    setLogoPositionY(scaled.logo_position_y);
    setLogoMarginTop(scaled.logo_margin_top);
    setTitleMarginTop(scaled.title_margin_top);
    setTitleMarginBottom(scaled.title_margin_bottom);
    setPortionWidth(scaled.portion_width);
    setPortionWidthSingleColumn(scaled.portion_width_single_column);
    setPortionLineHeight(scaled.portion_line_height);
    setTimeMarginRight(scaled.time_margin_right);
    setPortionVerticalSpacing(scaled.portion_vertical_spacing);
    setEntryMarginBottom(scaled.entry_margin_bottom);
    setMatchdayMarginBottom(scaled.matchday_margin_bottom);
    setSingleColumnExtraMargin(scaled.single_column_extra_margin);
  }

  function handlePresetChange(presetId) {
    setSelectedPresetId(presetId);
    if (!presetId) return;
    const preset = posterPresets.find((p) => String(p.id) === String(presetId));
    if (!preset) return;
    const rawW = parseInt(preset.width_px, 10);
    const rawH = parseInt(preset.height_px, 10);
    let orient = orientation;
    if (!orient) {
      orient = rawW > rawH ? "landscape" : "portrait";
      setOrientation(orient);
    }
    const [w, h] = resolvePresetDimensions(rawW, rawH, orient);
    setPageWidth(w);
    setPageHeight(h);
    if (preset.nr_of_columns) setNrOfColumns(preset.nr_of_columns);
    const scaled = calculateProportionalValues(w, h, orient);
    applyProportionalValues(scaled, { includeFonts: true });
    setShowAdvanced(true);
    setRecalcMessage("Proportions applied from preset");
    setTimeout(() => setRecalcMessage(""), 3000);
  }

  function handleRecalculate() {
    const w = parseInt(pageWidth, 10);
    const h = parseInt(pageHeight, 10);
    if (!w || !h) return;
    const orient = orientation || "landscape";
    const includeFonts = window.confirm(
      "Include font sizes in recalculation?\n\nOK = Recalculate everything (layout + fonts)\nCancel = Recalculate layout only (keep current fonts)"
    );
    const scaled = calculateProportionalValues(w, h, orient);
    applyProportionalValues(scaled, { includeFonts });
    setRecalcMessage(includeFonts ? "All proportions recalculated" : "Layout proportions recalculated (fonts kept)");
    setTimeout(() => setRecalcMessage(""), 3000);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    if (!name.trim()) return setError("Name is required.");
    if (!orientation) return setError("Orientation is required.");

    const payload = {
      name: name.trim(),
      bar_logo_type: barLogoType || "transparent",
      orientation,
      color1,
      color2,
      color3,
      color_bkg: backgroundColor,
      pps_slideshow: pps,
      video_slideshow: video,
      slide_duration: parseInt(duration, 10) || 15,
      system_font_id: systemFontId || null,
      nr_of_columns: parseInt(nrOfColumns, 10) || 2,
      ...(fontSizePrimary !== "" && { font_size_primary: parseInt(fontSizePrimary, 10) }),
      ...(fontSizeSecondary !== "" && { font_size_secondary: parseInt(fontSizeSecondary, 10) }),
      ...(fontSizeMatchday !== "" && { font_size_matchday: parseInt(fontSizeMatchday, 10) }),
      ...(fontSizeDateDay !== "" && { font_size_date_day: parseInt(fontSizeDateDay, 10) }),
      ...(fontSizeDateMonth !== "" && { font_size_date_month: parseInt(fontSizeDateMonth, 10) }),
      ...(fontSizeTitle !== "" && { font_size_title: parseInt(fontSizeTitle, 10) }),
      ...(pageWidth !== "" && { page_width: parseInt(pageWidth, 10) }),
      ...(pageHeight !== "" && { page_height: parseInt(pageHeight, 10) }),
      ...(pageMarginSide !== "" && { page_margin_side: parseInt(pageMarginSide, 10) }),
      ...(pageMarginSideExtra !== "" && { page_margin_side_extra: parseInt(pageMarginSideExtra, 10) }),
      ...(pageMarginTop !== "" && { page_margin_top: parseInt(pageMarginTop, 10) }),
      ...(pageMarginBottom !== "" && { page_margin_bottom: parseInt(pageMarginBottom, 10) }),
      ...(logoPositionY !== "" && { logo_position_y: parseInt(logoPositionY, 10) }),
      ...(logoMarginTop !== "" && { logo_margin_top: parseInt(logoMarginTop, 10) }),
      ...(titleMarginTop !== "" && { title_margin_top: parseInt(titleMarginTop, 10) }),
      ...(titleMarginBottom !== "" && { title_margin_bottom: parseInt(titleMarginBottom, 10) }),
      ...(portionWidth !== "" && { portion_width: parseInt(portionWidth, 10) }),
      ...(portionWidthSingleColumn !== "" && { portion_width_single_column: parseInt(portionWidthSingleColumn, 10) }),
      ...(portionLineHeight !== "" && { portion_line_height: parseInt(portionLineHeight, 10) }),
      ...(timeMarginRight !== "" && { time_margin_right: parseInt(timeMarginRight, 10) }),
      ...(portionVerticalSpacing !== "" && { portion_vertical_spacing: parseInt(portionVerticalSpacing, 10) }),
      ...(entryMarginBottom !== "" && { entry_margin_bottom: parseInt(entryMarginBottom, 10) }),
      ...(matchdayMarginBottom !== "" && { matchday_margin_bottom: parseInt(matchdayMarginBottom, 10) }),
      ...(entryMaxLines !== "" && { entry_max_lines: parseInt(entryMaxLines, 10) }),
      ...(minEntriesForSplit !== "" && { min_entries_for_split: parseInt(minEntriesForSplit, 10) }),
      ...(minEntriesPerColumnSplit !== "" && { min_entries_per_column_split: parseInt(minEntriesPerColumnSplit, 10) }),
      ...(singleColumnExtraMargin !== "" && { single_column_extra_margin: parseInt(singleColumnExtraMargin, 10) }),
      ...(maxZoomMultiplier !== "" && { max_zoom_multiplier: parseFloat(maxZoomMultiplier) }),
      // TODO: uncomment when Favorite reaches production (currently on staging in testing phase)
      // favorite_star_fill: favoriteStarFill,
      // favorite_star_stroke: favoriteStarStroke,
      // ...(favoriteStarStrokeWidth !== "" && { favorite_star_stroke_width: parseInt(favoriteStarStrokeWidth, 10) }),
      // ...(favoriteStarSize !== "" && { favorite_star_size: parseInt(favoriteStarSize, 10) }),
      // ...(favoriteStarGap !== "" && { favorite_star_gap: parseInt(favoriteStarGap, 10) }),
      bkg_image: fileBkg || undefined,
      example: fileExample || undefined,
      logo_sc: fileLogo || undefined,
      custom_font_file: fileCustomFont || undefined,
    };

    try {
      setSubmitting(true);
      await onSave?.(payload, isEdit ? poster?.id : null);
    } catch (err) {
      setError(err?.message || "Save failed.");
    } finally {
      setSubmitting(false);
    }
  }

  const rowGrid2 = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 };
  const rowGrid3 = { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16 };
  const rowGrid4 = { display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16 };

  const labelStyle = { display: "block", marginBottom: 4, fontSize: 13, fontWeight: 500 };
  const labelXsStyle = { display: "block", marginBottom: 4, fontSize: 11, fontWeight: 500 };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 1000 }}>
      {/* backdrop */}
      <div
        style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 1001 }}
        onClick={() => !submitting && onClose?.()}
        aria-hidden="true"
      />
      {/* dialog */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "center",
          padding: "32px 16px",
          zIndex: 1002,
          overflowY: "auto",
        }}
      >
        <div
          style={{
            background: "#fff",
            borderRadius: 8,
            width: "100%",
            maxWidth: 1100,
            boxShadow: "0 4px 24px rgba(0,0,0,0.18)",
          }}
        >
          {/* header */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "16px 24px",
              borderBottom: "1px solid #e5e7eb",
            }}
          >
            <h5 style={{ margin: 0, fontWeight: 600 }}>
              {isEdit ? `Edit Poster #${poster?.id}` : "Add new poster"}
            </h5>
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={() => !submitting && onClose?.()}
              disabled={submitting}
            >
              ✕ Close
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <div style={{ padding: 24, paddingBottom: 0 }}>
              {error && (
                <div className="alert alert-danger py-2" style={{ fontSize: 14 }}>
                  {error}
                </div>
              )}

              {/* layout: main col + preview col */}
              <div style={{ display: "grid", gap: 16, gridTemplateColumns: "1fr 180px", alignItems: "start" }}>

                {/* Name */}
                <div style={{ gridColumn: "1 / 2" }}>
                  <label style={labelStyle}>Name</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter poster name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    autoFocus
                  />
                </div>

                {/* Preview */}
                <aside
                  style={{
                    gridRow: "1 / span 2",
                    gridColumn: "2 / 3",
                    border: "1px solid #e5e7eb",
                    borderRadius: 8,
                    padding: 8,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    minHeight: 220,
                    background: "#f9fafb",
                  }}
                >
                  {isEdit && poster?.example ? (
                    <img
                      src={poster.example}
                      alt="Poster example"
                      style={{ maxWidth: "100%", maxHeight: 260, objectFit: "contain" }}
                    />
                  ) : (
                    <span style={{ opacity: 0.5, fontSize: 12 }}>No preview</span>
                  )}
                </aside>

                {/* Bar Logo Type */}
                <div style={{ gridColumn: "1 / 2" }}>
                  <label style={labelStyle}>Bar Logo Type</label>
                  <select
                    className="form-control"
                    value={barLogoType}
                    onChange={(e) => setBarLogoType(e.target.value)}
                  >
                    <option value="">Select logo type</option>
                    {BAR_LOGO_TYPES.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>

                {/* Preset + Orientation + Duration */}
                <div style={{ gridColumn: "1 / 3" }}>
                  <div style={rowGrid3}>
                    <div>
                      <label style={labelStyle}>Preset</label>
                      <select
                        className="form-control"
                        value={selectedPresetId}
                        onChange={(e) => handlePresetChange(e.target.value)}
                      >
                        <option value="">Select preset to apply...</option>
                        {posterPresets.map((p) => {
                          const w = parseFloat(p.width_value);
                          const h = parseFloat(p.height_value);
                          const wDisplay = Number.isInteger(w) ? w : w.toFixed(2);
                          const hDisplay = Number.isInteger(h) ? h : h.toFixed(2);
                          return (
                            <option key={p.id} value={p.id}>
                              {p.name} ({wDisplay}x{hDisplay}{p.unit})
                            </option>
                          );
                        })}
                      </select>
                      <div style={{ color: "#6b7280", fontSize: 11, marginTop: 4 }}>
                        Auto-fills dimensions and proportionally scales all layout values
                      </div>
                    </div>
                    <div>
                      <label style={labelStyle}>Orientation</label>
                      <select
                        className="form-control"
                        value={orientation}
                        onChange={(e) => setOrientation(e.target.value)}
                      >
                        <option value="">Select orientation</option>
                        {ORIENTATIONS.map((o) => (
                          <option key={o} value={o}>{o}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label style={labelStyle}>Slideshow duration (seconds)</label>
                      <input
                        type="number"
                        min={1}
                        className="form-control"
                        value={duration}
                        onChange={(e) => setDuration(e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                {/* Checkboxes */}
                <div style={{ gridColumn: "1 / 3" }}>
                  <div style={rowGrid2}>
                    <label style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 8 }}>
                      <input type="checkbox" checked={pps} onChange={(e) => setPps(e.target.checked)} />
                      <span style={{ fontSize: 14 }}>Enable PPS Slideshow</span>
                    </label>
                    <label style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 8 }}>
                      <input type="checkbox" checked={video} onChange={(e) => setVideo(e.target.checked)} />
                      <span style={{ fontSize: 14 }}>Enable Video Slideshow</span>
                    </label>
                  </div>
                </div>

                {/* Color selectors */}
                <div style={{ gridColumn: "1 / 3" }}>
                  <div style={rowGrid4}>
                    <div>
                      <label style={labelStyle}>Color1 (Day)</label>
                      <input
                        type="color"
                        className="form-control"
                        value={color1}
                        onChange={(e) => setColor1(e.target.value)}
                        style={{ height: 48, cursor: "pointer" }}
                      />
                    </div>
                    <div>
                      <label style={labelStyle}>Color2 (Month)</label>
                      <input
                        type="color"
                        className="form-control"
                        value={color2}
                        onChange={(e) => setColor2(e.target.value)}
                        style={{ height: 48, cursor: "pointer" }}
                      />
                    </div>
                    <div>
                      <label style={labelStyle}>Color3 (Year)</label>
                      <input
                        type="color"
                        className="form-control"
                        value={color3}
                        onChange={(e) => setColor3(e.target.value)}
                        style={{ height: 48, cursor: "pointer" }}
                      />
                    </div>
                    <div>
                      <label style={labelStyle}>Background Color</label>
                      <input
                        type="color"
                        className="form-control"
                        value={backgroundColor}
                        onChange={(e) => setBackgroundColor(e.target.value)}
                        style={{ height: 48, cursor: "pointer" }}
                      />
                    </div>
                  </div>
                </div>

                {/* File uploads */}
                <div style={{ gridColumn: "1 / 3" }}>
                  <div style={rowGrid3}>
                    <div>
                      <label style={labelStyle}>Background Image</label>
                      <input
                        type="file"
                        className="form-control-file"
                        accept="image/*"
                        onChange={(e) => setFileBkg(e.target.files?.[0] || null)}
                      />
                    </div>
                    <div>
                      <label style={labelStyle}>Example</label>
                      <input
                        type="file"
                        className="form-control-file"
                        accept="image/*"
                        onChange={(e) => setFileExample(e.target.files?.[0] || null)}
                      />
                    </div>
                    <div>
                      <label style={labelStyle}>SC Logo</label>
                      <input
                        type="file"
                        className="form-control-file"
                        accept="image/*"
                        onChange={(e) => setFileLogo(e.target.files?.[0] || null)}
                      />
                    </div>
                  </div>
                </div>

                {/* Advanced Settings Toggle */}
                <div style={{ gridColumn: "1 / 3" }}>
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm w-100"
                    onClick={() => setShowAdvanced(!showAdvanced)}
                    style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
                  >
                    <span>{showAdvanced ? "▲" : "▼"} Advanced Layout Settings</span>
                    <span style={{ fontSize: 11, opacity: 0.7 }}>
                      {showAdvanced ? "Hide" : "Show"} font sizes, dimensions, margins
                    </span>
                  </button>
                </div>

                {/* Advanced Settings */}
                {showAdvanced && (
                  <>
                    <div style={{ gridColumn: "1 / 3", borderTop: "1px solid #e5e7eb", paddingTop: 16, marginTop: 8 }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                          <p style={{ fontSize: 12, color: "#6b7280", margin: 0 }}>
                            Layout values scale proportionally based on page dimensions relative to orientation defaults.
                          </p>
                          {recalcMessage && (
                            <span style={{
                              fontSize: 12, fontWeight: 500, color: "#166534",
                              backgroundColor: "#dcfce7", padding: "4px 10px",
                              borderRadius: 4, whiteSpace: "nowrap",
                            }}>
                              ✓ {recalcMessage}
                            </span>
                          )}
                        </div>
                        <div style={{ display: "flex", gap: 8 }}>
                          <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm"
                            onClick={() => setShowDiagram(true)}
                            style={{ whiteSpace: "nowrap" }}
                          >
                            Layout Reference
                          </button>
                          <button
                            type="button"
                            className="btn btn-primary btn-sm"
                            onClick={handleRecalculate}
                            style={{ whiteSpace: "nowrap" }}
                          >
                            Recalculate proportions
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Font & Columns */}
                    <div style={{ gridColumn: "1 / 3" }}>
                      <h6 style={{ fontWeight: 600, marginBottom: 12 }}>Font & Columns</h6>
                      <div style={rowGrid3}>
                        <div>
                          <label style={labelStyle}>System Font</label>
                          <select
                            className="form-control"
                            value={systemFontId}
                            onChange={(e) => setSystemFontId(e.target.value)}
                          >
                            <option value="">Default (Asap)</option>
                            {systemFonts.map((f) => (
                              <option key={f.id} value={f.id}>{f.name}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label style={labelStyle}>Custom Font File</label>
                          <input
                            type="file"
                            className="form-control-file"
                            accept=".ttf,.otf,.woff,.woff2"
                            onChange={(e) => setFileCustomFont(e.target.files?.[0] || null)}
                          />
                        </div>
                        <div>
                          <label style={labelStyle}>
                            Number of Columns<InfoTooltip fieldKey="nr_of_columns" />
                          </label>
                          <input
                            type="number"
                            min={1}
                            max={5}
                            className="form-control"
                            value={nrOfColumns}
                            onChange={(e) => setNrOfColumns(e.target.value)}
                            placeholder="2"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Font Sizes */}
                    <div style={{ gridColumn: "1 / 3" }}>
                      <h6 style={{ fontWeight: 600, marginBottom: 12 }}>Font Sizes (px)</h6>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 12 }}>
                        {[
                          ["Primary", "font_size_primary", fontSizePrimary, setFontSizePrimary, "30"],
                          ["Secondary", "font_size_secondary", fontSizeSecondary, setFontSizeSecondary, "17"],
                          ["Matchday", "font_size_matchday", fontSizeMatchday, setFontSizeMatchday, "42"],
                          ["Date Day", "font_size_date_day", fontSizeDateDay, setFontSizeDateDay, "112"],
                          ["Date Month", "font_size_date_month", fontSizeDateMonth, setFontSizeDateMonth, "34"],
                          ["Title", "font_size_title", fontSizeTitle, setFontSizeTitle, "72"],
                        ].map(([label, key, val, setter, placeholder]) => (
                          <div key={key}>
                            <label style={labelXsStyle}>{label}<InfoTooltip fieldKey={key} /></label>
                            <input type="number" className="form-control form-control-sm" value={val} onChange={(e) => setter(e.target.value)} placeholder={placeholder} />
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Page Dimensions */}
                    <div style={{ gridColumn: "1 / 3" }}>
                      <h6 style={{ fontWeight: 600, marginBottom: 12 }}>Page Dimensions & Margins (px)</h6>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
                        {[
                          ["Width", "page_width", pageWidth, setPageWidth, "3008"],
                          ["Height", "page_height", pageHeight, setPageHeight, "1700"],
                          ["Margin Side", "page_margin_side", pageMarginSide, setPageMarginSide, "280"],
                          ["Margin Side Extra", "page_margin_side_extra", pageMarginSideExtra, setPageMarginSideExtra, "50"],
                          ["Margin Top", "page_margin_top", pageMarginTop, setPageMarginTop, "50"],
                          ["Margin Bottom", "page_margin_bottom", pageMarginBottom, setPageMarginBottom, "80"],
                          ["Logo Y Position", "logo_position_y", logoPositionY, setLogoPositionY, "1555"],
                          ["Logo Margin Top", "logo_margin_top", logoMarginTop, setLogoMarginTop, "150"],
                        ].map(([label, key, val, setter, placeholder]) => (
                          <div key={key}>
                            <label style={labelXsStyle}>{label}<InfoTooltip fieldKey={key} /></label>
                            <input type="number" className="form-control form-control-sm" value={val} onChange={(e) => setter(e.target.value)} placeholder={placeholder} />
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Title & Portion Settings */}
                    <div style={{ gridColumn: "1 / 3" }}>
                      <h6 style={{ fontWeight: 600, marginBottom: 12 }}>Title & Portion Settings (px)</h6>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 12 }}>
                        {[
                          ["Title Margin Top", "title_margin_top", titleMarginTop, setTitleMarginTop, "10"],
                          ["Title Margin Bottom", "title_margin_bottom", titleMarginBottom, setTitleMarginBottom, "160"],
                          ["Column Width", "portion_width", portionWidth, setPortionWidth, "1000"],
                          ["Single Column Width", "portion_width_single_column", portionWidthSingleColumn, setPortionWidthSingleColumn, "1400"],
                          ["Line Height", "portion_line_height", portionLineHeight, setPortionLineHeight, "15"],
                          ["Time Margin Right", "time_margin_right", timeMarginRight, setTimeMarginRight, "20"],
                          ["Vertical Spacing", "portion_vertical_spacing", portionVerticalSpacing, setPortionVerticalSpacing, "20"],
                          ["Single Col Extra Margin", "single_column_extra_margin", singleColumnExtraMargin, setSingleColumnExtraMargin, "50"],
                        ].map(([label, key, val, setter, placeholder]) => (
                          <div key={key}>
                            <label style={labelXsStyle}>{label}<InfoTooltip fieldKey={key} /></label>
                            <input type="number" className="form-control form-control-sm" value={val} onChange={(e) => setter(e.target.value)} placeholder={placeholder} />
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Entry Settings */}
                    <div style={{ gridColumn: "1 / 3" }}>
                      <h6 style={{ fontWeight: 600, marginBottom: 12 }}>Entry & Split Settings</h6>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 12 }}>
                        {[
                          ["Entry Margin Bottom", "entry_margin_bottom", entryMarginBottom, setEntryMarginBottom, "10"],
                          ["Matchday Margin Bottom", "matchday_margin_bottom", matchdayMarginBottom, setMatchdayMarginBottom, "3"],
                          ["Max Entry Lines", "entry_max_lines", entryMaxLines, setEntryMaxLines, ""],
                          ["Min Entries for Split", "min_entries_for_split", minEntriesForSplit, setMinEntriesForSplit, ""],
                          ["Min per Column Split", "min_entries_per_column_split", minEntriesPerColumnSplit, setMinEntriesPerColumnSplit, ""],
                          ["Max Zoom Multiplier", "max_zoom_multiplier", maxZoomMultiplier, setMaxZoomMultiplier, "1.6"],
                        ].map(([label, key, val, setter, placeholder]) => (
                          <div key={key}>
                            <label style={labelXsStyle}>{label}<InfoTooltip fieldKey={key} /></label>
                            <input type="number" className="form-control form-control-sm" value={val} onChange={(e) => setter(e.target.value)} placeholder={placeholder} step={key === "max_zoom_multiplier" ? "0.1" : "1"} />
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* TODO: uncomment when Favorite reaches production (currently on staging in testing phase) */}
                    {/* <div style={{ gridColumn: "1 / 3" }}>
                      <h6 style={{ fontWeight: 600, marginBottom: 12 }}>Favorite Star Styling</h6>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 12 }}>
                        <div>
                          <label style={labelXsStyle}>Fill Color</label>
                          <input type="color" className="form-control" value={favoriteStarFill} onChange={(e) => setFavoriteStarFill(e.target.value)} style={{ height: 38, cursor: "pointer" }} />
                        </div>
                        <div>
                          <label style={labelXsStyle}>Stroke Color</label>
                          <input type="color" className="form-control" value={favoriteStarStroke} onChange={(e) => setFavoriteStarStroke(e.target.value)} style={{ height: 38, cursor: "pointer" }} />
                        </div>
                        <div>
                          <label style={labelXsStyle}>Stroke Width</label>
                          <input type="number" className="form-control form-control-sm" value={favoriteStarStrokeWidth} onChange={(e) => setFavoriteStarStrokeWidth(e.target.value)} min={0} step={0.5} />
                        </div>
                        <div>
                          <label style={labelXsStyle}>Size (px)</label>
                          <input type="number" className="form-control form-control-sm" value={favoriteStarSize} onChange={(e) => setFavoriteStarSize(e.target.value)} min={0} />
                        </div>
                        <div>
                          <label style={labelXsStyle}>Gap (px)</label>
                          <input type="number" className="form-control form-control-sm" value={favoriteStarGap} onChange={(e) => setFavoriteStarGap(e.target.value)} min={0} />
                        </div>
                      </div>
                    </div> */}
                  </>
                )}
              </div>
            </div>

            {/* footer */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                gap: 8,
                padding: "16px 24px",
                borderTop: "1px solid #e5e7eb",
                marginTop: 24,
              }}
            >
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => !submitting && onClose?.()}
                disabled={submitting}
              >
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={submitting}>
                {submitting ? "Saving..." : "Save"}
              </button>
            </div>
          </form>
        </div>
      </div>

      {showDiagram && <LayoutDiagramModal onClose={() => setShowDiagram(false)} />}
    </div>
  );
}
